export interface DetectedDroneDto {
  droneId: string | null;
  estimatedLongitude: number | null;
  estimatedLatitude: number | null;
  confidence: number | null;
  sourceRayIds: string[];
  sourceDeviceIds: string[];
  sourceObserverIds: number[];
  firstSeenAtUtc: string | null;
  lastSeenAtUtc: string | null;
  averageRayMissDistanceMeters: number | null;
  observerCount: number | null;
}

export interface DetectedDroneViewModel {
  droneId: string;
  longitude: number;
  latitude: number;
  confidence: number;
  observerCount: number;
  averageRayMissDistanceMeters: number;
  sourceRayIds: string[];
  sourceDeviceIds: string[];
  sourceObserverIds: number[];
  firstSeenAtUtc: Date;
  lastSeenAtUtc: Date;
  isStale: boolean;
}

export interface DetectedDronesRecentQuery {
  time?: Date;
  lookbackSeconds?: number;
}

export function normalizeDetectedDroneResponse(
  response: DetectedDroneDto | DetectedDroneDto[] | null | undefined,
): DetectedDroneDto[] {
  if (!response) {
    return [];
  }

  return Array.isArray(response) ? response : [response];
}

export function toDetectedDroneViewModels(
  drones: DetectedDroneDto[],
  now = new Date(),
  staleAfterMs = 10000,
): DetectedDroneViewModel[] {
  return drones
    .map((drone) => toDetectedDroneViewModel(drone, now, staleAfterMs))
    .filter((drone): drone is DetectedDroneViewModel => drone !== null);
}

export function toDetectedDroneViewModel(
  drone: DetectedDroneDto,
  now = new Date(),
  staleAfterMs = 10000,
): DetectedDroneViewModel | null {
  const longitude = drone.estimatedLongitude;
  const latitude = drone.estimatedLatitude;

  if (!isValidDroneCoordinate(longitude, latitude)) {
    return null;
  }

  const validLatitude = latitude as number;
  const lastSeenAtUtc = parseDetectedDroneDate(drone.lastSeenAtUtc, now);
  const firstSeenAtUtc = parseDetectedDroneDate(drone.firstSeenAtUtc, lastSeenAtUtc);
  const confidence = clampConfidence(drone.confidence);
  const averageRayMissDistanceMeters = finiteOrDefault(drone.averageRayMissDistanceMeters, 0);
  const observerCount = Math.max(0, Math.round(finiteOrDefault(drone.observerCount, 0)));

  return {
    droneId: getDetectedDroneId(drone, lastSeenAtUtc),
    longitude,
    latitude: validLatitude,
    confidence,
    observerCount,
    averageRayMissDistanceMeters,
    sourceRayIds: normalizeStringArray(drone.sourceRayIds),
    sourceDeviceIds: normalizeStringArray(drone.sourceDeviceIds),
    sourceObserverIds: normalizeNumberArray(drone.sourceObserverIds),
    firstSeenAtUtc,
    lastSeenAtUtc,
    isStale: isDetectedDroneStale(lastSeenAtUtc, now, staleAfterMs),
  };
}

export function filterFreshDetectedDrones(
  drones: DetectedDroneViewModel[],
  now = new Date(),
  staleAfterMs = 10000,
): DetectedDroneViewModel[] {
  return drones.filter((drone) => !isDetectedDroneStale(drone.lastSeenAtUtc, now, staleAfterMs));
}

export function isValidDroneCoordinate(
  longitude: number | null | undefined,
  latitude: number | null | undefined,
): longitude is number {
  return (
    typeof longitude === 'number' &&
    typeof latitude === 'number' &&
    Number.isFinite(longitude) &&
    typeof latitude === 'number' &&
    Number.isFinite(latitude) &&
    longitude >= -180 &&
    longitude <= 180 &&
    latitude >= -90 &&
    latitude <= 90
  );
}

export function isDetectedDroneStale(lastSeenAtUtc: Date, now = new Date(), staleAfterMs = 10000): boolean {
  return now.getTime() - lastSeenAtUtc.getTime() > staleAfterMs;
}

export function droneConfidenceToRadius(confidence: number | null | undefined): number {
  const normalizedConfidence = clampConfidence(confidence);

  return 7 + Math.round((normalizedConfidence / 100) * 4);
}

export function clampConfidence(confidence: number | null | undefined): number {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) {
    return 0;
  }

  return Math.min(Math.max(confidence, 0), 100);
}

function getDetectedDroneId(drone: DetectedDroneDto, lastSeenAtUtc: Date): string {
  const droneId = drone.droneId?.trim();

  if (droneId) {
    return droneId;
  }

  const roundedLongitude = drone.estimatedLongitude?.toFixed(5) ?? 'unknown-lon';
  const roundedLatitude = drone.estimatedLatitude?.toFixed(5) ?? 'unknown-lat';

  return `${roundedLongitude}:${roundedLatitude}:${lastSeenAtUtc.toISOString()}`;
}

function parseDetectedDroneDate(value: string | null | undefined, fallback: Date): Date {
  if (!value) {
    return fallback;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? fallback : parsedDate;
}

function finiteOrDefault(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeStringArray(values: readonly unknown[] | null | undefined): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
}

function normalizeNumberArray(values: readonly unknown[] | null | undefined): number[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => (typeof value === 'number' ? value : Number(value)))
    .filter((value) => Number.isFinite(value));
}
